<template>
    <slot name="header_close"/>

    <div class="wrapper_dialog">
        <h3 class="title">
            {{ $t("dialog.webhooks.dialog_title") }}
            <div class="number-block">
                <img class="icon" :src="messengerIcon"/>
                <span>{{ number }}</span>
            </div>
        </h3>

        <form class="content" novalidate @submit.prevent="updateWebhooks">
            <div class="field">
                <p class="subtitle">{{ $t("dialog.webhooks.inbox_hook") }}</p>
                <input
                    v-model="form.incoming"
                    :class="{ input_error: !!errors.incoming }"
                    placeholder="http(s)://example.com"
                />
                <div class="input-errors" :class="{ 'is-shown': !!errors.incoming }">
                    {{ errors.incoming }}
                </div>
            </div>

            <div class="field">
                <p class="subtitle">{{ $t("dialog.webhooks.status_hook") }}</p>
                <input
                    v-model="form.status"
                    :class="{ input_error: !!errors.status }"
                    placeholder="http(s)://example.com"
                />
                <div class="input-errors" :class="{ 'is-shown': !!errors.status }">
                    {{ errors.status }}
                </div>
            </div>

            <div class="action_container">
                <button-loading
                    type="submit"
                    :loading="button.loading"
                    :disabled="button.disabled || !isSubmittable"
                >
                    <template v-if="dialogParams.action === 'edit'">
                        {{ $t("dialog.webhooks.button") }}
                    </template>
                    <template v-else>
                        {{ $t("dialog.webhooks.add_button") }}
                    </template>
                </button-loading>
            </div>
        </form>
    </div>
</template>

<script>
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import ToastContent from "@/components/Recurring/ToastContent.vue";

import {mapGetters, mapState, mapMutations} from "vuex";
import {withProps} from "mosha-vue-toastify";
import useApi from "@/services/useApi";

import {validateWebhookUrl} from "@/helpers/dialogValidation";

export default {
    name: "WebhookAddEditDialog",

    components: {
        ButtonLoading,
    },

    // !! TODO: проверить логику selectIntegrationFalse в логике темлейта
    data() {
        return {
            settings: {
                numberFalse: true,
                selectIntegrationFalse: true,
            },
            form: {
                incoming: "",
                status: "",
            },
            initialForm: {
                incoming: "",
                status: "",
            },
            button: {
                loading: false,
                disabled: false,
            },
        };
    },

    computed: {
        ...mapGetters("auth", ["getAuthHeader"]),

        errors() {
            return {
                incoming: validateWebhookUrl(this.form.incoming, this.$t),
                status: validateWebhookUrl(this.form.status, this.$t),
            };
        },

        isSubmittable() {
            const filled = !!this.form.incoming?.trim() && !!this.form.status?.trim();
            const valid = !this.errors.incoming && !this.errors.status;
            const changed =
                this.form.incoming !== this.initialForm.incoming ||
                this.form.status !== this.initialForm.status;

            return filled && valid && changed;
        },

        ...mapState({
            dialogParams: (state) => state.dialogParams,
        }),

        number() {
            return this.dialogParams?.el?.number || this.dialogParams?.info?.number;
        },

        messenger() {
            return this.dialogParams?.el?.source || this.dialogParams?.info?.messenger;
        },

        messengerIcon() {
            return this.messenger
                ? require(`@/assets/icons/${this.messenger}.svg`).default
                : "";
        },
    },

    methods: {
        ...mapMutations(["closeDialog"]),

        setDialogParams() {
            if (this.dialogParams?.action === "add") return;
            const webhook = new Array(2);
            this.dialogParams?.info?.webhooks.forEach((el) => {
                el.type === "inbox" ? (webhook[0] = el.url) : (webhook[1] = el.url);
            });
            this.form.incoming = this.dialogParams?.el?.webhooks?.inbox || webhook[0] || "";
            this.form.status = this.dialogParams?.el?.webhooks?.status || webhook[1] || "";
            this.initialForm = {...this.form};
        },

        onToogleButtonLoading() {
            if (this.button.loading) {
                this.button.loading = false;
                this.button.disabled = false;
                return;
            }

            this.button.loading = true;
            this.button.disabled = true;
        },

        async updateWebhooks() {
            const {api, headers} = useApi();
            if (!this.isSubmittable) return;

            const number = this.dialogParams?.el?.number || this.dialogParams?.info?.number;

            const payload = {
                messenger: this.dialogParams?.el?.source || this.dialogParams?.info?.messenger,
                webhooks: [
                    {
                        type: "inbox",
                        url: this.form.incoming,
                    },
                    {
                        type: "status",
                        url: this.form.status,
                    },
                ],
                number,
            };

            // В зависимости от типа определяем endpoint и method
            const method = this.dialogParams?.action === "add" ? "post" : "put";
            const endpoint = method === "post" ? "/api/numbers/webhooks" : `/api/numbers/webhooks/${number}`;

            this.onToogleButtonLoading();
            await api[method](endpoint, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage?.getItem("token")}`,
                },
            })
                .then(() => {
                    this.$moshaToast(
                        withProps(ToastContent, {
                            msg: this.$t("dialog.webhooks.success"),
                            type: "success",
                        }),
                        {
                            hideProgressBar: true,
                        }
                    );
                    if (!!this.dialogParams?.next) {
                        this.$store.commit("setDialog", this.dialogParams.next);
                        this.$store.state.dialogParams.info.webhooks = payload.webhooks;
                        this.$store.commit("setNextDialog", "");
                    } else {
                        this.closeDialog();
                    }
                })
                .catch((e) => {
                    this.$moshaToast(
                        withProps(ToastContent, {
                            msg: this.$t("dialog.webhooks.error"),
                            type: "error",
                        }),
                        {
                            hideProgressBar: true,
                            timeout: 10000,
                        }
                    );
                })
                .finally(() => {
                    this.$store.dispatch("numbers/loadNumbersByMessenger", {
                        messenger: this.dialogParams.info.messenger,
                        visibility: this.dialogParams.info.visibility,
                    });
                    this.onToogleButtonLoading();
                });
        },
    },

    mounted() {
        this.setDialogParams();
    },
};
</script>

<style lang="scss" scoped>
@use "@/styles/dialog-form.scss";
</style>
